import {
    Emoji,
    emojiCategories,
    EmojiCategory,
    getEmojisByCategory,
} from "@/assets/emojis";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Editor } from "@tiptap/react";
import { Smile } from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

// Empty mail body renders as a placeholder paragraph stripped on send (MailInput).
const PLACEHOLDER_PARAGRAPH_REGEX = /<p[^>]*id="placeholder"[^>]*>.*?<\/p>/g;

interface EmojiPickerProps {
    editor?: Editor;
    className?: string;
    onEmojiSelect?: (emoji: Emoji) => void;
    triggerIcon?: string;
}

interface EmojiButtonProps {
    emoji: string;
    onClick: () => void;
}

const EmojiButton = ({ emoji, onClick }: EmojiButtonProps) => (
    <Button
        className="h-8 w-8 p-0 text-lg"
        // Don't blur the editor on click — keeps the caret, avoids the placeholder flash.
        onClick={onClick}
        onMouseDown={(e) => e.preventDefault()}
        variant="ghost"
    >
        {emoji}
    </Button>
);

const EmojiPicker = ({
    editor,
    className,
    onEmojiSelect,
    triggerIcon,
}: EmojiPickerProps) => {
    const [activeCategory, setActiveCategory] = useState(emojiCategories[0].id);
    const [isOpen, setIsOpen] = useState(false);

    const handleEmojiClick = (emoji: Emoji) => {
        if (editor) {
            const html = editor.getHTML();
            if (html.includes('id="placeholder"')) {
                // Replace the placeholder so the emoji lands in the body, not the signature.
                editor
                    .chain()
                    .setContent(
                        html.replace(PLACEHOLDER_PARAGRAPH_REGEX, "<p></p>"),
                    )
                    .focus("start")
                    .insertContent(emoji.symbol)
                    .run();
            } else {
                editor.chain().focus().insertContent(emoji.symbol).run();
            }
        }
        onEmojiSelect?.(emoji);
    };

    return (
        // Close on outside click/Escape, but stay open while picking emojis.
        <Popover onOpenChange={setIsOpen} open={isOpen}>
            <PopoverTrigger asChild>
                <Button
                    className={className}
                    onClick={(e) => e.stopPropagation()}
                    size="sm"
                    variant="ghost"
                >
                    {triggerIcon && (
                        <span className="text-lg">{triggerIcon}</span>
                    )}
                    {!triggerIcon && <Smile className="h-4 w-4" />}
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-72 p-2"
                onFocusOutside={(e) => e.preventDefault()}
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <Tabs
                    className="w-full"
                    defaultValue={activeCategory}
                    onValueChange={(value) =>
                        setActiveCategory(value as EmojiCategory)
                    }
                >
                    <TabsList className="mb-2 grid h-12 w-full grid-cols-8">
                        {emojiCategories.map((category) => (
                            <TabsTrigger
                                key={category.id}
                                className="flex items-center justify-center"
                                value={category.id}
                            >
                                <span className="text-lg">{category.icon}</span>
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    {emojiCategories.map((category) => (
                        <TabsContent
                            key={category.id}
                            className="mt-0 grid grid-cols-8 gap-2"
                            value={category.id}
                        >
                            {getEmojisByCategory(category.id).map((element) => (
                                <EmojiButton
                                    key={element.name}
                                    emoji={element.emoji.symbol}
                                    onClick={() =>
                                        handleEmojiClick(element.emoji)
                                    }
                                />
                            ))}
                        </TabsContent>
                    ))}
                </Tabs>
            </PopoverContent>
        </Popover>
    );
};

export default EmojiPicker;
